import React, { useEffect, useState, useCallback } from 'react';
import API from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const ENTITY_TYPES = ['transaction','cardholder','card','category','cost_center','allocation_rule','import_batch','statement_period','export_batch','approval'];

const ACTION_BADGE = {
  CREATE: 'bg-emerald-100 text-emerald-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-rose-100 text-rose-700',
  ALLOCATE: 'bg-violet-100 text-violet-700',
  IMPORT_UPLOAD: 'bg-amber-100 text-amber-700',
  IMPORT_PROMOTE: 'bg-teal-100 text-teal-700',
  EXPORT_GENERATED: 'bg-teal-100 text-teal-700',
  EXPORT_DOWNLOAD: 'bg-blue-100 text-blue-700',
  SUBMIT_FOR_APPROVAL: 'bg-amber-100 text-amber-700',
  APPROVAL_APPROVE: 'bg-emerald-100 text-emerald-700',
  APPROVAL_REJECT: 'bg-rose-100 text-rose-700',
  UPDATE_STATUS: 'bg-slate-100 text-slate-700',
};

export default function AuditLogPage() {
  const [events, setEvents] = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(true);
  const [actorFilter, setActorFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (actorFilter) params.actor = actorFilter;
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity_type = entityFilter;
      const res = await API.get('/audit', { params });
      setEvents(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, actorFilter, actionFilter, entityFilter]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-bold">Audit Log</h1>
      <p className="text-sm text-slate-500">Immutable record of all system actions</p>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Filter by actor email…"
          value={actorFilter}
          onChange={e => { setActorFilter(e.target.value); setPage(1); }}
          className="h-8 text-sm w-52"
          data-testid="audit-log-actor-filter"
        />
        <Input
          placeholder="Filter by action…"
          value={actionFilter}
          onChange={e => { setActionFilter(e.target.value); setPage(1); }}
          className="h-8 text-sm w-40"
        />
        <Select value={entityFilter} onValueChange={v => { setEntityFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="h-8 text-xs w-44">
            <SelectValue placeholder="All entities" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All entities</SelectItem>
            {ENTITY_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-slate-400 flex items-center">{total} events</span>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="audit-log-table">
          <thead><tr>
            <th>Timestamp</th><th>Actor</th><th>Role</th><th>Action</th>
            <th>Entity Type</th><th>Entity ID</th><th>Details</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8"><RefreshCw size={16} className="animate-spin inline text-slate-400" /></td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No audit events</td></tr>
            ) : events.map(e => (
              <tr key={e.id}>
                <td className="text-xs whitespace-nowrap font-mono text-slate-500">
                  {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                </td>
                <td className="text-xs">{e.actor_email}</td>
                <td className="text-xs text-slate-500">{e.actor_role}</td>
                <td>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${ACTION_BADGE[e.action] || 'bg-slate-100 text-slate-600'}`}>
                    {e.action}
                  </span>
                </td>
                <td className="text-xs font-mono">{e.entity_type}</td>
                <td className="text-xs font-mono text-slate-400">{e.entity_id?.slice(-8) || '—'}</td>
                <td className="text-xs text-slate-500 max-w-xs truncate" title={JSON.stringify(e.details)}>
                  {Object.keys(e.details || {}).length > 0 ? JSON.stringify(e.details).slice(0, 80) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Page {page} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}><ChevronLeft size={13} /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page === pages}><ChevronRight size={13} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
